import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImportTimesheetService } from 'app/core/services/import-timesheet.service';
import { PrivateService } from 'app/core/services/private.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-dialog-edit-time-entry',
  templateUrl: './dialog-edit-time-entry.component.html',
  styleUrls: ['./dialog-edit-time-entry.component.scss']
})
export class DialogEditTimeEntryComponent implements OnInit {
  timeEntryForm: FormGroup;
  isSaving: boolean = false;
  isNewEntry: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DialogEditTimeEntryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private importTimesheetService: ImportTimesheetService,
    private toastr: ToastrService
  ) {
    // Determine if this is a new entry
    this.isNewEntry = data.isNewEntry || !data.element;

    this.timeEntryForm = this.fb.group({
      dateIn: ['', Validators.required],
      timeIn: ['', Validators.required],
      dateOut: ['', Validators.required],
      timeOut: ['', Validators.required]
    }, { validators: this.dateTimeRangeValidator });
  }

  ngOnInit(): void {
    if (!this.isNewEntry && this.data.element?.times && this.data.element.times.length >= 2) {
      // Edit mode - populate existing data
      const timeInFull = this.data.element.times[0]; // e.g., "2026-01-23 09:00:30 AM"
      const timeOutFull = this.data.element.times[1]; // e.g., "2026-01-23 05:30:00 PM"

      const { date: dateIn, time: timeIn24 } = this.extractDateAndTime(timeInFull);
      const { date: dateOut, time: timeOut24 } = this.extractDateAndTime(timeOutFull);

      this.timeEntryForm.patchValue({
        dateIn: dateIn,
        timeIn: timeIn24,
        dateOut: dateOut,
        timeOut: timeOut24
      });
    } else {
      // Add mode - set default values (current date and empty times)
      const today = new Date().toISOString().split('T')[0];
      this.timeEntryForm.patchValue({
        dateIn: today,
        timeIn: '',
        dateOut: today,
        timeOut: ''
      });
    }
  }

  /**
   * Extract date and time from a datetime string
   * Input: "2026-01-23 09:00:30 AM" or "2026-01-23 09:00:30"
   * Output: { date: "2026-01-23", time: "09:00:30" }
   */
  extractDateAndTime(dateTimeStr: string): { date: string; time: string } {
    if (!dateTimeStr) return { date: '', time: '' };

    // Check if the string contains AM/PM
    const hasAMPM = dateTimeStr.includes('AM') || dateTimeStr.includes('PM');

    if (hasAMPM) {
      // Format: "2026-01-23 09:00:30 AM"
      const parts = dateTimeStr.trim().split(' ');
      const date = parts[0]; // "2026-01-23"
      const time12h = parts[1]; // "09:00:30"
      const modifier = parts[2]; // "AM" or "PM"

      const time24h = this.convertTo24Hour(time12h, modifier);
      return { date, time: time24h };
    } else {
      // Format: "2026-01-23 09:00:30" (already 24-hour)
      const [date, time] = dateTimeStr.split(' ');
      return { date: date || '', time: time || '' };
    }
  }

  /**
   * Convert 12-hour format to 24-hour format
   * Input: time = "09:00:30", modifier = "PM"
   * Output: "21:00:30"
   */
  convertTo24Hour(time12h: string, modifier: string): string {
    if (!time12h) return '';

    const timeParts = time12h.split(':');
    let hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    const seconds = timeParts[2] || '00';

    if (modifier === 'PM' && hours !== 12) {
      hours += 12;
    } else if (modifier === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}:${seconds}`;
  }

  /**
   * Convert 24-hour format to 12-hour format with AM/PM
   * Input: "21:00:30"
   * Output: "9:00:30 PM"
   */
  convertTo12Hour(time24h: string): string {
    if (!time24h) return '';

    const timeParts = time24h.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    const seconds = timeParts[2] || '00';
    const modifier = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;

    return `${displayHour}:${minutes}:${seconds} ${modifier}`;
  }

  /**
   * Custom validator to ensure Time Out is after Time In
   */
  dateTimeRangeValidator(control: AbstractControl): ValidationErrors | null {
    const dateIn = control.get('dateIn')?.value;
    const timeIn = control.get('timeIn')?.value;
    const dateOut = control.get('dateOut')?.value;
    const timeOut = control.get('timeOut')?.value;

    if (!dateIn || !timeIn || !dateOut || !timeOut) {
      return null;
    }

    // Combine date and time for comparison
    const dateTimeIn = new Date(`${dateIn}T${timeIn}`);
    const dateTimeOut = new Date(`${dateOut}T${timeOut}`);

    if (dateTimeOut <= dateTimeIn) {
      control.get('dateOut')?.setErrors({ invalidDateTimeRange: true });
      control.get('timeOut')?.setErrors({ invalidDateTimeRange: true });
      return { invalidDateTimeRange: true };
    } else {
      // Clear the errors if the range is now valid
      const dateOutControl = control.get('dateOut');
      const timeOutControl = control.get('timeOut');

      if (dateOutControl?.hasError('invalidDateTimeRange')) {
        dateOutControl.setErrors(null);
      }
      if (timeOutControl?.hasError('invalidDateTimeRange')) {
        timeOutControl.setErrors(null);
      }
    }

    return null;
  }

  onSave(): void {
    if (this.timeEntryForm.invalid) {
      this.timeEntryForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    const dateIn = this.timeEntryForm.value.dateIn;
    const timeIn = this.timeEntryForm.value.timeIn;
    const dateOut = this.timeEntryForm.value.dateOut;
    const timeOut = this.timeEntryForm.value.timeOut;

    // Create full datetime strings by combining date and time
    const timeInDateTime = `${dateIn} ${timeIn}`;
    const timeOutDateTime = `${dateOut} ${timeOut}`;

    // Convert to 12-hour format for display
    const timeIn12h = `${dateIn} ${this.convertTo12Hour(timeIn)}`;
    const timeOut12h = `${dateOut} ${this.convertTo12Hour(timeOut)}`;

    if (this.isNewEntry) {
      // Add new time entry
      const payload = {
        timesheetId: this.data.timesheetId,
        timeIn: timeInDateTime,
        timeOut: timeOutDateTime
      };

      console.log('Payload for new entry:', payload);

      this.importTimesheetService.createTimesheetLine(payload).subscribe({
        next: (response) => {
          this.showNotification('Time entry added successfully');
          this.dialogRef.close({
            success: true,
            isNewEntry: true,
            timesheetLineID: response.timesheetLineID || response.id,
            timeIn: timeIn12h,
            timeOut: timeOut12h,
            date: dateIn,
            dateIn: dateIn,
            dateOut: dateOut,
            dayOfWeek: this.getDayOfWeek(dateIn)
          });
        },
        error: (error) => {
          console.error('Error adding time entry:', error);
          this.showNotificationError('Failed to add time entry');
          this.isSaving = false;
        }
      });
    } else {
      // Update existing time entry
      const payload = {
        timesheetLineID: this.data.timesheetLineID,
        timeIn: timeInDateTime,
        timeOut: timeOutDateTime
      };

      console.log('Payload for update:', payload);

      this.importTimesheetService.updateTimesheetLine(payload).subscribe({
        next: (response) => {
          this.showNotification('Time entry updated successfully');
          this.dialogRef.close({
            success: true,
            isNewEntry: false,
            timeIn: timeIn12h,
            timeOut: timeOut12h,
            dateIn: dateIn,
            dateOut: dateOut
          });
        },
        error: (error) => {
          console.error('Error updating time entry:', error);
          this.showNotificationError('Failed to update time entry');
          this.isSaving = false;
        }
      });
    }
  }

  /**
   * Get day of week from date string
   */
  getDayOfWeek(dateStr: string): string {
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  showNotification(text: string): void {
    this.toastr.success('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-success alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }

  showNotificationError(text: string): void {
    this.toastr.error('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-danger alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }
}